// Before / after slider front-end: a draggable divider that wipes between two
// stacked images ( "after" base layer, "before" clipped from the left ).
// Mirrors the Lovable BeforeAfter interaction — pointer-drag plus keyboard on
// the slider handle. Loaded on-demand ( view_script ) only when the block is on
// the page. The wipe is user-driven ( not an auto animation ), so it stays
// active under prefers-reduced-motion; only the handle's hover micro-motion is
// neutralised in CSS.

const SELECTOR = '.b-before-after';
const STEP = 2;

/**
 * Wire up a single before/after slider.
 *
 * @param {HTMLElement} root Block root element.
 *
 * @return {void}
 */
const initSlider = ( root ) => {

	const viewport = root.querySelector( '.b-before-after__viewport' );

	if ( ! viewport || viewport.classList.contains( 'b-before-after__viewport--empty' ) ) {

		return;

	}

	const before  = viewport.querySelector( '.b-before-after__layer--before' );
	const divider = viewport.querySelector( '.b-before-after__divider' );
	const handle  = viewport.querySelector( '.b-before-after__handle' );

	if ( ! before || ! divider || ! handle ) {

		return;

	}

	let position = parseFloat( viewport.dataset.position );

	if ( Number.isNaN( position ) ) {

		position = 50;

	}

	let dragging = false;

	/**
	 * Reflect the current position onto the clip, divider, handle and ARIA.
	 *
	 * @return {void}
	 */
	const apply = () => {

		before.style.clipPath = `inset(0 ${ 100 - position }% 0 0)`;
		divider.style.left = `${ position }%`;
		handle.style.left = `${ position }%`;
		handle.setAttribute( 'aria-valuenow', String( Math.round( position ) ) );

	};

	/**
	 * Map a viewport-relative pointer X to a 0-100 position.
	 *
	 * @param {number} clientX Pointer X in client coordinates.
	 *
	 * @return {void}
	 */
	const setFromClientX = ( clientX ) => {

		const rect = viewport.getBoundingClientRect();
		const pct = ( ( clientX - rect.left ) / rect.width ) * 100;
		position = Math.max( 0, Math.min( 100, pct ) );
		apply();

	};

	/**
	 * Continue the wipe while the pointer is held down.
	 *
	 * @param {PointerEvent} event Pointer move event.
	 *
	 * @return {void}
	 */
	const onMove = ( event ) => {

		if ( ! dragging ) {

			return;

		}

		setFromClientX( event.clientX );

	};

	/**
	 * Release the drag.
	 *
	 * @return {void}
	 */
	const onUp = () => {

		dragging = false;

	};

	viewport.addEventListener( 'pointerdown', ( event ) => {

		dragging = true;
		setFromClientX( event.clientX );

	} );

	window.addEventListener( 'pointermove', onMove, { passive: true } );
	window.addEventListener( 'pointerup', onUp );

	handle.addEventListener( 'keydown', ( event ) => {

		if ( event.key === 'ArrowLeft' ) {

			position = Math.max( 0, position - STEP );

		} else if ( event.key === 'ArrowRight' ) {

			position = Math.min( 100, position + STEP );

		} else if ( event.key === 'Home' ) {

			position = 0;

		} else if ( event.key === 'End' ) {

			position = 100;

		} else {

			return;

		}

		event.preventDefault();
		apply();

	} );

	apply();

};

/**
 * Initialise every before/after block on the page.
 *
 * @return {void}
 */
const init = () => {

	document.querySelectorAll( SELECTOR ).forEach( initSlider );

};

if ( document.readyState !== 'loading' ) {

	init();

} else {

	document.addEventListener( 'DOMContentLoaded', init );

}
